import React, { useState, useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { Dropdown, Spinner } from "react-bootstrap";
import StockCard from "./StockCard";
import MINavigation from "./MINavigation";
import ShareHoldings from "./ShareHoldings";
import AnalystRatings from "./AnalystRatings";
import IntelligenceUpdates from "./IntelligenceUpdates";
import Metrics from "./Metrics";
import SentimentAnalysis from "./SentimentAnalysis";
import Filings from "./Filings";
import Insiders from "./Insiders";
import { getNewsPairs, getMarketIntelligence, getMarketNews, getMetrics, getFilings, getInsiders, resetMarketIntelligence } from "../../actions/market-intelligence-action";

const TAB_KEYS = {
    SHAREHOLDINGS: "SHAREHOLDINGS",
    ANALYST_RATINGS: "ANALYST_RATINGS",
    UPDATES: "UPDATES",
    FILINGS: "FILINGS",
    INSIDERS: "INSIDERS",
    METRICS: "METRICS",
    SENTIMENT: "SENTIMENT"
};

const MarketIntelligenceContent = (props) => {
    
    const { getNewsPairs, getMarketIntelligence, getMarketNews, getMetrics, getFilings, getInsiders, resetMarketIntelligence, isModal = false, close, defaultPairId } = props;
    const [activeTab, setActiveTab] = useState("SHAREHOLDINGS");
    const newsPairs = useSelector((state) => state.marketIntelligence.newsPairs) || [];
    const marketData = useSelector((state) => state.marketIntelligence.marketIntelligence);
    const marketNews = useSelector((state) => state.marketIntelligence.marketNews) || [];
    const metricsData = useSelector((state) => state.marketIntelligence.metrics);
    const filingsData = useSelector((state) => state.marketIntelligence.filings);
    const insidersData = useSelector((state) => state.marketIntelligence.insiders);
    const loading = useSelector((state) => state.marketIntelligence.loading);
    const [selectedStock, setSelectedStock] = useState(defaultPairId || "");
    const [searchFilter, setSearchFilter] = useState("");

    useEffect(() => {
        if(defaultPairId) {
             setSelectedStock(String(defaultPairId));
             getMarketIntelligence(defaultPairId);
             getMarketNews(defaultPairId);
             getMetrics(defaultPairId);
             getFilings(defaultPairId);
             getInsiders(defaultPairId);
        }
    }, [defaultPairId, getMarketIntelligence, getMarketNews, getMetrics, getFilings, getInsiders]);

    useEffect(() => {
        getNewsPairs();
    }, [getNewsPairs]);

    useEffect(() => {
        if (marketData) {
             // Logic to update local state if needed, or pass directly.
             // We can rely on marketData for StockCard
        }
    }, [marketData]);

    const handleStockSelect = (pairId) => {
        const selected = String(pairId);
        setSelectedStock(selected);
        if (selected) {
            resetMarketIntelligence();
            getMarketIntelligence(selected);
            getMarketNews(selected);
            getMetrics(selected);
            getFilings(selected);
            getInsiders(selected);
        }
    };
    // Modal.setAppElement('#root'); // Removed as this is no longer a modal wrapper

    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
    };

    useEffect(() => {
        // If no stock selected and we have pairs, select first one
        if (!selectedStock && newsPairs.length > 0) {
            const firstStock = newsPairs[0];
            setSelectedStock(String(firstStock.pair_id));
            getMarketIntelligence(firstStock.pair_id);
            getMarketNews(firstStock.pair_id);
            getMetrics(firstStock.pair_id);
            getFilings(firstStock.pair_id);
            getInsiders(firstStock.pair_id);
        }
    }, [newsPairs, selectedStock, getMarketIntelligence, getMarketNews, getMetrics, getFilings, getInsiders]);

    // Helper to check if market data is empty/invalid
    const isEmptyData = !marketData || Object.keys(marketData).length === 0;

  return (
        <div className={`stock-dashboard-wrapper market-intel-dropdown-div ${!isModal ? 'mi-page-style' : ''}`}>
            <div className={`d-flex align-items-center justify-content-between ${isModal ? "border-0 pb-0 pt-4 px-4" : "p-4 border-bottom"}`}>
            <div className="d-flex align-items-center gap-3">
                <h4 className="mb-0 fw-bold text-dark">Market Intelligence</h4>
                
                <Dropdown onSelect={handleStockSelect}>
                    <Dropdown.Toggle 
                        id="dropdown-stock-select"
                        variant="light"
                        className="stock-dropdown-mi"
                         // Using light variant for white/light gray background
                    >
                        {selectedStock 
                            ? (newsPairs.find(p => String(p.pair_id) === String(selectedStock))?.name || "Select Stock") 
                            : "Select Stock"
                        }
                    </Dropdown.Toggle>

                    <Dropdown.Menu style={{ width: '400px', maxHeight: '300px', overflowY: 'auto' }}>
                        <div className="px-2 py-1 sticky-top bg-white border-bottom">
                            <input 
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search..."
                                onChange={(e) => setSearchFilter(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    border: '1px solid #E5E7EB'
                                }}
                            />
                        </div>
                    {(() => {
                        const filteredStocks = newsPairs.filter(stock => 
                            !searchFilter || 
                            stock.name.toLowerCase().includes(searchFilter.toLowerCase()) || 
                            (stock.company && stock.company.toLowerCase().includes(searchFilter.toLowerCase()))
                        );

                        return filteredStocks.length > 0 ? (
                            filteredStocks.map((stock) => (
                                <Dropdown.Item key={stock.pair_id} eventKey={stock.pair_id}>
                                    <div className="d-flex align-items-center">
                                        {stock.icon && <img src={stock.icon} alt={stock.name} style={{width: 20, height: 20, marginRight: 8, borderRadius: '50%'}} onError={(e) => {e.target.style.display='none'}} />}
                                        <span className="fw-medium">{stock.name}</span>
                                        <span className="text-muted ms-2 small" style={{fontSize: '0.8em'}}>({stock.company})</span>
                                    </div>
                                </Dropdown.Item>
                            ))
                        ) : (
                            <div className="px-3 py-2 text-muted small text-center">
                                No results found. Please email <a href="mailto:admin@stockhero.ai">admin@stockhero.ai</a> to add this stock to Market Intel.
                            </div>
                        );
                    })()}
                    </Dropdown.Menu>
                </Dropdown>

            </div>
            {isModal && (
            <div className="cursor-pointer p-2" onClick={close}>
                 <i className="fa fa-times text-muted fs-5"></i>
            </div>
            )}
            </div>

            <div className={`${isModal ? "px-4 pb-4" : "p-4"}`}>
            {loading ? (
                 <div className="d-flex flex-column align-items-center justify-content-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : isEmptyData ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                    <p className="mb-0 fs-5">No result found for this stock</p>
                </div>
            ) : (
                <>
                    <div className="mt-5 mb-4">
                        <StockCard 
                            price={marketData ? marketData.current_price : 0} 
                            change={marketData ? marketData.current_price_change_1day : 0} 
                            changePercent={marketData ? marketData.current_price_change_percentage_1day : 0} 
                            name={marketData ? marketData.company_name : ""}
                            symbol={selectedStock ? (newsPairs.find(p => String(p.pair_id) === String(selectedStock))?.name) : ""}
                            logo={selectedStock ? (newsPairs.find(p => String(p.pair_id) === String(selectedStock))?.icon) : ""}
                        />
                    </div>
                    <div className="mt-3">
                        <MINavigation activeTab={activeTab} onTabChange={handleTabChange} tabs={TAB_KEYS}></MINavigation>
                    </div>
                    {activeTab === TAB_KEYS.SHAREHOLDINGS && <ShareHoldings data={marketData}></ShareHoldings>}
                    {activeTab === TAB_KEYS.ANALYST_RATINGS && <AnalystRatings data={marketData}></AnalystRatings>}
                    {activeTab === TAB_KEYS.UPDATES && <IntelligenceUpdates data={marketNews}></IntelligenceUpdates>}
                    {activeTab === TAB_KEYS.FILINGS && <Filings data={filingsData}></Filings>}
                    {activeTab === TAB_KEYS.INSIDERS && <Insiders data={insidersData}></Insiders>}
                    {activeTab === TAB_KEYS.METRICS && <Metrics data={metricsData}></Metrics>}
                    {activeTab === TAB_KEYS.SENTIMENT && <SentimentAnalysis pairId={selectedStock}></SentimentAnalysis>}
                </>
            )}
            </div>
        </div>
  );
};

export default connect(null, { getNewsPairs, getMarketIntelligence, getMarketNews, getMetrics, getFilings, getInsiders, resetMarketIntelligence })(MarketIntelligenceContent);
